/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on
 * an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import ColorPickerControl from 'src/explore/components/controls/ColorPickerControl';
import React from 'react';
import { styled, SupersetTheme, t, useTheme } from '@superset-ui/core';
import { Form, FormItem } from 'src/components/Form';
import Select from 'src/components/Select/Select';
import { Col, Row } from 'src/components';
import { Input } from 'src/components/Input';
import Button from 'src/components/Button';
import { TextColorRule, TEXT_COMPARATOR } from './types';

const FullWidthInput = styled(Input)`
  width: 100%;
`;

const JustifyEnd = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const colorSchemeOptions = (theme: SupersetTheme) =>
  [
    { value: theme.colors.success.light1, label: t('浅绿色') },
    { value: theme.colors.alert.light1, label: t('黄色') },
    { value: theme.colors.error.light1, label: t('浅红色') },
    { value: theme.colors.success.dark1, label: t('深绿色') },
    { value: theme.colors.alert.dark1, label: t('深棕色') },
    { value: theme.colors.error.dark1, label: t('深红色') },
    { value: theme.colors.primary.base, label: t('蓝色') },
    { value: theme.colors.grayscale.base, label: t('灰色') },
    { value: 'custom', label: t('自定义颜色') },
  ].map(option => ({
    ...option,
    label:
      typeof option.label === 'string'
        ? option.label
        : JSON.stringify(option.label),
    value:
      typeof option.value === 'string'
        ? option.value
        : JSON.stringify(option.value),
  }));

const operatorOptions = [
  { value: TEXT_COMPARATOR.EQUALS, label: t('等于') },
  { value: TEXT_COMPARATOR.CONTAINS, label: t('包含') },
  { value: TEXT_COMPARATOR.STARTS_WITH, label: t('开始于') },
  { value: TEXT_COMPARATOR.ENDS_WITH, label: t('结束于') },
];

const rulesRequired = [{ required: true, message: t('Required') }];

export const TextFormattingPopoverContent = ({
  config,
  onChange,
  columns = [],
}: {
  config?: TextColorRule;
  onChange: (config: TextColorRule) => void;
  columns: { label: string; value: string }[];
}) => {
  const theme = useTheme();
  const colorScheme = colorSchemeOptions(theme);

  const handleFormFinish = (values: TextColorRule) => {
    const newValues = { ...values };

    if (newValues.colorScheme === 'custom') {
      if (
        typeof newValues.customColor === 'object' &&
        newValues.customColor !== null &&
        'r' in newValues.customColor &&
        'g' in newValues.customColor &&
        'b' in newValues.customColor
      ) {
        const { r, g, b } = newValues.customColor;
        const redHex = r.toString(16).padStart(2, '0').toUpperCase();
        const greenHex = g.toString(16).padStart(2, '0').toUpperCase();
        const blueHex = b.toString(16).padStart(2, '0').toUpperCase();
        newValues.colorScheme = `#${redHex}${greenHex}${blueHex}`;
        delete newValues.customColor;
      } else {
        console.error('Invalid customColor format');
      }
    }
    onChange(newValues);
  };

  return (
    <Form
      onFinish={handleFormFinish}
      initialValues={config}
      requiredMark={false}
      layout="vertical"
    >
      <Row gutter={12}>
        <Col span={12}>
          <FormItem
            name="column"
            label={t('Column')}
            rules={rulesRequired}
            initialValue={columns[0]?.value}
          >
            <Select ariaLabel={t('Select column')} options={columns} />
          </FormItem>
        </Col>
        <Col span={12}>
          <FormItem
            name="operator"
            label={t('Operator')}
            rules={rulesRequired}
            initialValue={operatorOptions[0].value}
          >
            <Select ariaLabel={t('Operator')} options={operatorOptions} />
          </FormItem>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <FormItem
            name="targetValue"
            label={t('文本值')}
            rules={rulesRequired}
          >
            <FullWidthInput placeholder={t('请输入文本值')} />
          </FormItem>
        </Col>
        <Col span={12}>
          <FormItem
            name="colorScheme"
            label={t('Color scheme')}
            rules={rulesRequired}
            initialValue={colorScheme[0].value}
          >
            <Select ariaLabel={t('Color scheme')} options={colorScheme} />
          </FormItem>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <FormItem
            noStyle
            shouldUpdate={(prev, cur) => prev.colorScheme !== cur.colorScheme}
          >
            {({ getFieldValue }) => {
              const selectedColor = getFieldValue('colorScheme');
              return (
                <>
                  {selectedColor === 'custom' ? (
                    <>
                      <FormItem
                        name="customColor"
                        label={t('颜色选择器')}
                        rules={rulesRequired}
                        initialValue={
                          config?.customColor || {
                            r: parseInt(
                              theme.colors.primary.base.slice(1, 3),
                              16,
                            ),
                            g: parseInt(
                              theme.colors.primary.base.slice(3, 5),
                              16,
                            ),
                            b: parseInt(
                              theme.colors.primary.base.slice(5, 7),
                              16,
                            ),
                            a: 100,
                          }
                        }
                      >
                        <ColorPickerControl disableAlpha />
                      </FormItem>
                    </>
                  ) : (
                    <>
                      <Row gutter={8}>
                        <Col span={12}>
                          <label
                            style={{
                              padding: '0 0 4px',
                              margin: '0',
                            }}
                            htmlFor="colorScheme"
                            className="ant-form-item-label"
                            title={t('颜色预览')}
                          >
                            {t('颜色预览')}
                          </label>
                          <div
                            style={{
                              width: '100%',
                              height: '30px',
                              backgroundColor: selectedColor,
                              border: `1px solid ${theme.colors.grayscale.light2}`,
                              borderRadius: '4px',
                              marginBottom: '8px',
                            }}
                          />
                        </Col>
                        <Col span={12}>
                          <label
                            style={{
                              padding: '0 0 4px',
                              margin: '0',
                            }}
                            htmlFor="colorScheme"
                            className="ant-form-item-label"
                            title={t('字体预览')}
                          >
                            {t('字体预览')}
                          </label>
                          <div
                            style={{
                              width: '100%',
                              height: '30px',
                              display: 'flex',
                              justifyContent: 'flex-start',
                              alignItems: 'center',
                              color: selectedColor,
                              backgroundColor: theme.colors.grayscale.light5,
                              border: `1px solid ${theme.colors.grayscale.light2}`,
                              borderRadius: '4px',
                              marginBottom: '8px',
                            }}
                          >
                            <span
                              style={{
                                padding: '0 11px',
                                margin: '0',
                                fontWeight: 550,
                              }}
                            >
                              {t('文本示例')}
                            </span>
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              );
            }}
          </FormItem>
        </Col>
      </Row>
      <FormItem>
        <JustifyEnd>
          <Button htmlType="submit" buttonStyle="primary">
            {t('Apply')}
          </Button>
        </JustifyEnd>
      </FormItem>
    </Form>
  );
};
